/**
 * AusschlusslistePage — Administration › Ausschlussliste (Fix 6, decision B).
 *
 * Broker CRUD over the customer-suppression list (/admin/suppression), gated by the new
 * suppression.manage permission. A row matches an inbound sender by DOMAIN and/or company
 * (UID / firm name); a match blocks VN genesis (no Firma/Person is created) while the
 * email itself stays visible and parked. This is DISTINCT from the internal AML blacklist.
 *
 * Mirrors the TeamsPage shape (header + list + create/edit dialog); wired to the EXISTING
 * /admin/suppression endpoints via apiClient.admin.*.
 *
 * [Source: apps/web/src/features/administration/pages/TeamsPage.tsx] (page + dialog pattern)
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useAdminSuppression } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { Button, Dialog, LoadingState, ErrorState, EmptyState, TextInput } from '../components';
import { Badge } from '../../../components/ui/Badge';
import type { SuppressionEntryDto } from '../types';

const meta = ADMIN_PAGE_META.ausschlussliste;

/** The primary human-readable label for a row (domain > company name > uid). */
function rowLabel(r: SuppressionEntryDto): string {
  return r.domain ?? r.company_name ?? r.uid ?? '-';
}

// ── Create / edit dialog ─────────────────────────────────────────────────────
function SuppressionDialog({
  entry,
  onClose,
}: {
  entry: SuppressionEntryDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = entry != null;
  const [domain, setDomain] = useState(entry?.domain ?? '');
  const [uid, setUid] = useState(entry?.uid ?? '');
  const [companyName, setCompanyName] = useState(entry?.company_name ?? '');
  const [reason, setReason] = useState(entry?.reason ?? '');
  const [error, setError] = useState<string | null>(null);

  const hasMatchField =
    domain.trim() !== '' || uid.trim() !== '' || companyName.trim() !== '';

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.adminSuppression() });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        await apiClient.admin.updateSuppression(entry.id, {
          domain: domain.trim() || null,
          uid: uid.trim() || null,
          company_name: companyName.trim() || null,
          reason: reason.trim() || null,
        });
      } else {
        await apiClient.admin.createSuppression({
          domain: domain.trim() || undefined,
          uid: uid.trim() || undefined,
          company_name: companyName.trim() || undefined,
          reason: reason.trim() || undefined,
        });
      }
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const del = useMutation({
    mutationFn: () => apiClient.admin.deleteSuppression(entry!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? rowLabel(entry) : 'Eintrag hinzufügen'}
      description="Kundinnen und Kunden ausschliessen: deren E-Mails bleiben sichtbar, erzeugen aber keinen Kunden."
      footer={
        <div className="flex flex-1 items-center justify-between">
          <div>
            {isEdit && (
              <Button
                variant="ghost"
                className="text-status-danger hover:bg-status-danger-bg"
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                Löschen
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button disabled={save.isPending || !hasMatchField} onClick={() => save.mutate()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Mindestens eines der Felder Domain, UID oder Firmenname angeben.
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Domain</label>
          <TextInput
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="firma.ch"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">UID</label>
          <TextInput
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            placeholder="CHE-123.456.789"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Firmenname</label>
          <TextInput value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Grund (optional)
          </label>
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
    </Dialog>
  );
}

export function AusschlusslistePage() {
  const { can } = useEffectivePermissions();
  const canManage = can('suppression.manage');
  const listQuery = useAdminSuppression();
  const [dialog, setDialog] = useState<{ entry: SuppressionEntryDto | null } | null>(null);

  const rows = listQuery.data ?? [];

  return (
    <div>
      <AdminPageHeader
        title={meta.title}
        description={meta.description}
        actions={
          canManage ? (
            <Button onClick={() => setDialog({ entry: null })}>Neuer Eintrag</Button>
          ) : undefined
        }
      />
      {listQuery.isLoading ? (
        <LoadingState />
      ) : listQuery.isError ? (
        <ErrorState />
      ) : rows.length === 0 ? (
        <EmptyState label="Noch keine Einträge. Domains oder Firmen ausschliessen: deren E-Mails erzeugen keinen Kunden." />
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  {rowLabel(r)}
                  {r.status !== 'active' && <Badge variant="neutral">Inaktiv</Badge>}
                </span>
                {r.reason != null && r.reason !== '' && (
                  <span className="block truncate text-xs text-muted-foreground">{r.reason}</span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => setDialog({ entry: r })}>
                {canManage ? 'Bearbeiten' : 'Ansehen'}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {dialog != null && (
        <SuppressionDialog entry={dialog.entry} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
